import type { ReactElement } from 'react';

import { CompanyScopeNote } from '@/components/documents/CompanyScopeNote';
import { CardFrame, QueryBody } from '@/components/ui/card-frame';
import { usePermission } from '@/hooks/usePermission';
import { useDocumentCompanyAccess } from '@/lib/document-company';
import { plural } from '@/lib/plural';

import { participantsText, reworkTotal } from '../presentation';
import { useEntryRework } from '../use-rework';
import { ReworkEventList } from './ReworkEventList';

// Tarjeta "Reprocesos" de la ficha (spec 2026-10-14 §5 D1): cada devolución del
// documento y el conteo por persona y rol, incluidos los que no tuvieron reprocesos.
// Solo con quality:rework:read (so=380). Se consulta bajo la empresa activa: si no es la
// del documento, la tarjeta lo explica en vez de consultar.

export function ReworkHistoryCard({
  entryId,
  companyId,
}: {
  entryId: string;
  /** Empresa del documento. */
  companyId: string;
}): ReactElement | null {
  const canRead = usePermission('quality:rework:read');
  const access = useDocumentCompanyAccess(companyId);
  const query = useEntryRework(entryId, {
    companyId: access.kind === 'active' ? companyId : null,
    enabled: canRead && access.kind === 'active',
  });
  if (!canRead) {
    return null;
  }
  if (access.kind !== 'active') {
    return (
      <CardFrame title="Reprocesos" className="d3">
        <CompanyScopeNote access={access} subject="sus reprocesos" />
      </CardFrame>
    );
  }
  const count = query.data
    ? plural(reworkTotal(query.data.participants), 'devolución', 'devoluciones')
    : undefined;
  return (
    <CardFrame title="Reprocesos" count={count} className="d3">
      <QueryBody
        query={query}
        isEmpty={(d) => d.events.length === 0}
        emptyText="Ninguna versión volvió a una etapa anterior."
      >
        {(d) => (
          <>
            {d.participants.length > 0 && (
              <ul className="rw-people" aria-label="Reprocesos por persona y rol">
                {participantsText(d.participants).map((p) => (
                  <li key={p.key}>{p.text}</li>
                ))}
              </ul>
            )}
            <ReworkEventList events={d.events} />
            {d.eventsTruncated && (
              <p className="cardfoot">Se muestran las 500 devoluciones más recientes.</p>
            )}
          </>
        )}
      </QueryBody>
    </CardFrame>
  );
}
